import { createContext } from 'react';

import type { Command } from '../types/command';

/**
 * Registration primitive. Each `useCommands` call owns a stable
 * `sourceKey: symbol` and reconciles its current command set against
 * the registry by source:
 *
 *   - Ids previously owned by this source but not in `commands`:
 *     removed from the registry.
 *   - Ids in `commands` that already exist in the registry: their
 *     entry is overwritten in place. Property-iteration position
 *     in the underlying Record is preserved, so any section bucket
 *     anchored on that id doesn't drift.
 *   - Ids new to the registry: appended.
 *
 * The cleanup primitive (`clearSource`) is only used on component
 * unmount. Dep-change re-runs of `useCommands` call
 * `setCommandsForSource` again, which patches in place.
 *
 * That asymmetry is what lets a consumer's data update mutate
 * command content without dragging the section's palette position
 * to the bottom of the listbox.
 */
export type SetCommandsForSource = (sourceKey: symbol, commands: Command[]) => void;
export type ClearSource = (sourceKey: symbol) => void;

/**
 * Context value for the command registry.
 *
 * Split into two concerns:
 *   - registration callbacks: stable identities. Read by `useCommands`
 *     call sites, which subscribe but never re-render on registry
 *     churn.
 *   - `commands` + handlers: the read surface. Re-renders subscribers
 *     when the registry mutates.
 *
 * The split lives in the provider (two contexts) so dispatch-only
 * consumers don't churn on every register/unregister.
 */
export interface CommandRegistryContextValue {
  /** Flat registry keyed by command id. Preserves insertion order. */
  commands: Record<string, Command>;
}

/**
 * Registration context. Stable callback identities so dispatch-only
 * consumers (every `useCommands` call site) never re-render on
 * registry churn.
 */
export interface CommandRegistrationContextValue {
  setCommandsForSource: SetCommandsForSource;
  clearSource: ClearSource;
}

export const CommandRegistryContext = createContext<CommandRegistryContextValue | null>(null);
export const CommandRegistrationContext = createContext<CommandRegistrationContextValue | null>(null);
